/**
 * THE TOWN SEARCH - `searchBirthPlaces` (wave 26), the platform's own geocoding behind one typed
 * field. The author types, the platform answers with settlements, and only a settlement PICKED from
 * the answer is ever sent: its `birthPlaceId` is the stable id the platform turns into coordinates
 * itself, and a typed name is a label that can never give one.
 *
 * Written for the birth place of "Your chart" (../components/BirthDataSection.tsx) and kept apart
 * from it so another field can search the same towns: "Primary location" (design 25,
 * ../components/PrimaryLocationField.tsx) asks in English, because its label is what readers see.
 *
 * ON A LAPTOP the stack's geocoding is a stand-in that knows one town, Gomel; any other answer is
 * empty there and the field says so. The real service answers everywhere else.
 */
import { useEffect, useState } from 'react';
import { useCabinetApi } from '../../cabinet/api';
import type { OperationResult } from '../../cabinet/api';

/** One settlement, as the platform answers it: the id, the label and its zone today. */
export type PlaceSuggestion = OperationResult<'searchBirthPlaces'>['birthPlaces'][number];

/** How long typing must pause before the place search is asked, so a word is one request. */
export const PLACE_SEARCH_PAUSE_MS = 250;

/** The fewest characters worth asking: on production 2 letters took 2-15 s, 3 under 0.5 s. */
export const PLACE_SEARCH_MIN = 3;

/** `searchBirthPlaces`' `q` is at most 100 characters (code points, as the contract counts). */
export const PLACE_SEARCH_MAX = 100;

/** A BCP 47 tag as `searchBirthPlaces`' `lang` admits one. */
const LANGUAGE_TAG = /^[A-Za-z]{2,3}(-[A-Za-z0-9]{1,8})*$/;

/** The browser's language, for the place labels - omitted when it is not a tag the operation takes. */
function labelLanguage(): string | undefined {
  const tag = typeof navigator === 'undefined' ? '' : navigator.language;
  return tag.length <= 35 && LANGUAGE_TAG.test(tag) ? tag : undefined;
}

export type PlaceSearchStatus = 'idle' | 'searching' | 'ready' | 'failed';

/**
 * The place suggestions for what is in the field. Asked after `PLACE_SEARCH_PAUSE_MS` of quiet and
 * from `PLACE_SEARCH_MIN` characters; not asked again for the label of the place just picked; a
 * slower answer to an older text is dropped (the request is aborted and its result ignored).
 * `lang`, when given, is the labels' language; otherwise the browser's.
 */
export function usePlaceSearch(
  text: string,
  picked: PlaceSuggestion | null,
  lang?: string,
): { readonly places: readonly PlaceSuggestion[]; readonly status: PlaceSearchStatus } {
  const api = useCabinetApi();
  const [places, setPlaces] = useState<readonly PlaceSuggestion[]>([]);
  const [status, setStatus] = useState<PlaceSearchStatus>('idle');
  const typed = [...text.trim()].slice(0, PLACE_SEARCH_MAX).join('');
  const settled = picked !== null && picked.birthPlace === text;

  useEffect(() => {
    if (settled || [...typed].length < PLACE_SEARCH_MIN) {
      setStatus('idle');
      return undefined;
    }
    const controller = new AbortController();
    // Set in the effect body, so a StrictMode re-run gets its own - never a cleanup-only ref.
    let live = true;
    const timer = setTimeout(() => {
      setStatus('searching');
      const language = lang ?? labelLanguage();
      api
        .searchBirthPlaces(
          { q: typed, limit: 5, ...(language === undefined ? {} : { lang: language }) },
          controller.signal,
        )
        .then((answer) => {
          if (live) {
            setPlaces(answer.birthPlaces);
            setStatus('ready');
          }
        })
        .catch(() => {
          // An abort is this effect's own doing; `live` is already false for it.
          if (live) {
            setPlaces([]);
            setStatus('failed');
          }
        });
    }, PLACE_SEARCH_PAUSE_MS);
    return () => {
      live = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [api, typed, settled, lang]);

  return { places, status };
}
